import { getUUID } from '@statsig/client-core';
import {
  AnalyticsEvent,
  ButtonComponentV2,
  CreateTabapayAuthTransferInput,
  CreateTabapayAuthTransferResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import { logTransactionAuthEvent } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useState } from 'react';
import CreateTabapayAuthTransfer from 'services/graphql/mutations/CreateTabapayAuthTransfer';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

const useCreateTabapayAuth = () => {
  const [result, executeMutation] = useMutation<Mutation>(
    CreateTabapayAuthTransfer,
  );
  const { segmentClient } = useAnalyticsConfiguration();
  const [errorScreen, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { fetching: mutationFetching, error: mutationError } = result;
  const idempotencyId = getUUID();

  const createTabapayTransactionAuth = async (
    input: CreateTabapayAuthTransferInput,
    flowName?: string,
  ) => {
    try {
      const { data } = await executeMutation({ idempotencyId, input });
      if (!data?.createTabapayAuthTransfer) {
        throw new Error('Mutation did not return data');
      } else if (
        data?.createTabapayAuthTransfer?.__typename ===
        CreateTabapayAuthTransferResultTypenames.CreateTabapayAuthTransferSuccess
      ) {
        logTransactionAuthEvent(
          'Create Transaction Auth',
          'Success',
          segmentClient,
          flowName,
        );
        return data.createTabapayAuthTransfer;
      } else if (
        data?.createTabapayAuthTransfer?.__typename ===
        CreateTabapayAuthTransferResultTypenames.TabaPayCustomError
      ) {
        const customErrorScreen = getTabapayErrorScreen(
          data?.createTabapayAuthTransfer,
        );
        logTransactionAuthEvent(
          'Create Transaction Auth',
          'Tabapay Custom Error',
          segmentClient,
          flowName,
        );
        if (customErrorScreen) {
          setErrorScreen(customErrorScreen);
        }
      } else if (
        data?.createTabapayAuthTransfer?.__typename ===
        CreateTabapayAuthTransferResultTypenames.CreateTabapayAuthTransferError
      ) {
        logTransactionAuthEvent(
          'Create Transaction Auth',
          'Generic_Error',
          segmentClient,
          flowName,
        );
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: data.createTabapayAuthTransfer.title,
          messageBody: data.createTabapayAuthTransfer.message,
        });
      }
    } catch (error) {
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    }
  };

  return {
    createTabapayTransactionAuth,
    isLoading: mutationFetching,
    errorScreen,
    error: mutationError,
  };
};

export default useCreateTabapayAuth;
